/**
 * Presentation-only finance helpers. Labels and destination paths are
 * derived from server enums / DTO fields — no client-side accounting.
 */
import { toEnglishDigits } from './phoneNumber.ts';
import { formatRialAmountFa, amountDisplayUnitLabel, type AmountDisplayUnit } from '@sakhtar/money';

export type { AmountDisplayUnit };

export type SubledgerKind = 'ar' | 'ap';

export const OPEN_ITEM_SOURCE_LABEL: Record<string, string> = {
  manual: 'ثبت دستی',
  sales_handoff: 'فروش',
  procurement_handoff: 'خرید',
  reversal: 'برگشت',
};

export const TREASURY_TXN_STATUS_LABEL: Record<string, string> = {
  draft: 'پیش‌نویس',
  posted: 'ثبت‌شده',
  reversed: 'برگشت‌خورده',
};

export const VAT_SOURCE_KIND_LABEL: Record<string, string> = {
  ar_open_item: 'دریافتنی',
  ap_open_item: 'پرداختنی',
};

export const VAT_STATUS_LABEL: Record<string, string> = {
  draft: 'پیش‌نویس',
  posted: 'ثبت‌شده',
  reversed: 'برگشت‌خورده',
};

export const OPEN_ITEM_STATUS_TONE: Record<string, 'neutral' | 'warn' | 'info' | 'success'> = {
  draft: 'neutral',
  open: 'info',
  partial: 'warn',
  settled: 'success',
  reversed: 'neutral',
};

export function amountUnitLabel(unit: AmountDisplayUnit): 'ریال' | 'تومان' {
  return amountDisplayUnitLabel(unit);
}

export function formatRialFa(digits: string, unit: AmountDisplayUnit): string {
  return formatRialAmountFa(digits, unit);
}

/** Digit grouping plus the matching display-unit label (ریال/تومان). */
export function formatRialFaWithUnit(digits: string, unit: AmountDisplayUnit): string {
  return `${formatRialAmountFa(digits, unit)} ${amountDisplayUnitLabel(unit)}`;
}

/** Table/KPI header that names the same unit the cell formatter uses. */
export function amountColumnHeader(title: string, unit: AmountDisplayUnit): string {
  return `${title} (${amountDisplayUnitLabel(unit)})`;
}

/**
 * Normalizes a money `<input>`'s raw keystroke value into ASCII digits
 * only. Required because these fields echo the typed amount back through
 * `formatRialFa` (Persian digits + `٬` separators, e.g. "۱٬۲۳۴") — typing a
 * second character then hands `onChange` a value mixing that Persian text
 * with the new ASCII keystroke, and a naive `.replace(/\D/g, '')` treats
 * every Persian digit as non-digit noise and discards it, silently
 * dropping everything typed before the latest keystroke.
 */
export function parseRialInput(value: string): string {
  return toEnglishDigits(value).replace(/\D/g, '');
}

export function sourceTypeLabel(sourceType: string): string {
  return OPEN_ITEM_SOURCE_LABEL[sourceType] ?? 'منبع ثبت‌شده';
}

/**
 * Cross-module destination for an open-item source, only when the DTO
 * sourceType is an explicit handoff. Manual/unknown sources stay unlinked.
 */
export function subledgerSourceHref(
  kind: SubledgerKind,
  sourceType: string,
  sourceReference: string,
  base: string,
): string | null {
  if (!sourceReference) return null;
  if (kind === 'ar' && sourceType === 'sales_handoff') {
    return `${base}/crm/orders/${encodeURIComponent(sourceReference)}`;
  }
  if (kind === 'ap' && sourceType === 'procurement_handoff') {
    return `${base}/procurement/orders/${encodeURIComponent(sourceReference)}`;
  }
  return null;
}

export function journalHref(base: string, journalId: string): string {
  return `${base}/finance/journals/${encodeURIComponent(journalId)}`;
}

export function openItemHref(base: string, kind: SubledgerKind, openItemId: string): string {
  return `${base}/finance/${kind}/${encodeURIComponent(openItemId)}`;
}

export function vatSourceHref(base: string, sourceKind: string, sourceId: string): string | null {
  if (sourceKind === 'ar_open_item') return openItemHref(base, 'ar', sourceId);
  if (sourceKind === 'ap_open_item') return openItemHref(base, 'ap', sourceId);
  return null;
}

export function agingBucketLabel(bucket: string): string {
  const labels: Record<string, string> = {
    current: 'جاری',
    '1-30': '۱ تا ۳۰ روز',
    '31-60': '۳۱ تا ۶۰ روز',
    '61-90': '۶۱ تا ۹۰ روز',
    '90+': 'بیش از ۹۰ روز',
  };
  return labels[bucket] ?? 'بازهٔ سنی';
}

export function labeled(map: Record<string, string>, value: string, fallback: string): string {
  return map[value] ?? fallback;
}

export function vatStatusLabel(status: string): string {
  return labeled(VAT_STATUS_LABEL, status, 'وضعیت ثبت‌شده');
}

export function treasuryTxnStatusLabel(status: string): string {
  return labeled(TREASURY_TXN_STATUS_LABEL, status, 'وضعیت ثبت‌شده');
}

/** Open + partial items are the working AR/AP KPI; draft/settled/reversed stay out. */
export function workingOpenItemCount(items: readonly { status: string }[]): number {
  return items.filter((item) => item.status === 'open' || item.status === 'partial').length;
}

/**
 * Sums working (open/partial) `openBalanceRial` digit strings as `BigInt` —
 * never `Number(...)` per-row, since summing several rows through floating
 * point can lose precision an authoritative Rial total may not (AGENTS.md
 * §8). Returns a digit string, formattable via {@link formatRialFa} or
 * convertible to `Number` once at a chart-datapoint boundary — never summed
 * again after that conversion.
 */
export function workingOpenBalanceRial(
  items: readonly { status: string; openBalanceRial: string }[],
): string {
  return items
    .filter((item) => item.status === 'open' || item.status === 'partial')
    .reduce((sum, item) => sum + BigInt(item.openBalanceRial), 0n)
    .toString();
}

export function matchingSourceReference<T extends { sourceReference: string | null }>(
  items: readonly T[],
  sourceReference: string,
): T[] {
  return items.filter((item) => item.sourceReference === sourceReference);
}

export function movementIdByNumber(
  movements: readonly { movementId: string; movementNumber: string }[],
  movementNumber: string,
): string | null {
  return movements.find((row) => row.movementNumber === movementNumber)?.movementId ?? null;
}
